<!--
  ContactRowEditor — one line of a list of contacts, open for editing.

  The line itself is the typed box (the email, number, link or place); a value
  that doesn't read for its kind stays in the box, marked, until it's fixed.
  Expanded, the rest of the contact sits under it: its label (a social's is
  its platform, set from the link) and whether it's primary, and for a new
  contact on a field of any kind, its kind. A stored contact keeps its kind.
  The add line is the same editor on a new contact, empty until typed in.

  Emits `update` with the whole row on every change that reads, `enter` on
  Enter in the box, and `toggle` on a click on the line outside its box and
  buttons. Slots: `icon` leads the line (the contact's icon by default), `end`
  closes it. Laid out by RowsEditor.
-->
<template>
  <div class="contact-row-editor">
    <div class="head" @click="onHeadClick">
      <slot name="icon">
        <font-awesome-icon :icon="icon" class="kind-icon" fixed-width />
      </slot>
      <input
        ref="box"
        class="typed"
        :class="{ 'is-invalid': !!error }"
        :value="typed"
        :placeholder="placeholder"
        :aria-label="kindLabel"
        @input="onTyped(($event.target as HTMLInputElement).value)"
        @keydown.enter.prevent="emit('enter')"
      />
      <slot name="end" />
    </div>

    <div v-if="expanded" class="fields">
      <span v-if="error" class="hint is-error">{{ error }}</span>

      <div v-if="kindEditable && adding" class="kinds" role="group" aria-label="Kind">
        <button
          v-for="k in CONTACT_KINDS"
          :key="k.key"
          type="button"
          class="kind"
          :class="{ 'is-selected': k.key === row.contact_type }"
          :aria-pressed="k.key === row.contact_type"
          @click="setKind(k.key)"
        >
          <font-awesome-icon :icon="k.icon" fixed-width />
          <span>{{ k.label }}</span>
        </button>
      </div>

      <div class="inputs">
        <input
          v-if="!socials"
          class="input is-small label"
          :value="row.label ?? ''"
          placeholder="Label (work, home…)"
          aria-label="Label"
          @input="emitRow({ label: ($event.target as HTMLInputElement).value || null })"
        />
        <label class="check">
          <input
            type="checkbox"
            :checked="!!row.is_primary"
            @change="emitRow({ is_primary: ($event.target as HTMLInputElement).checked })"
          />
          Primary
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue"
import { type Field, parseValue, socialPlatformOf } from "@auger/core/fields"
import {
  CONTACT_KINDS,
  contactIcon,
  type ContactKind,
  type ContactRow,
} from "@auger/ui/utils/contacts"

const props = withDefaults(
  defineProps<{
    row: ContactRow
    /** A socials field: the label is the platform, read from the link. */
    socials?: boolean
    /** Whether a new contact can pick its kind (a field of any kind). */
    kindEditable?: boolean
    /** Show the rest of the contact under the line. */
    expanded?: boolean
    /** A new contact: the box starts empty until something is typed. */
    adding?: boolean
    placeholder?: string
    autofocus?: boolean
    /** Typed text to start with (a key pressed on the cell). */
    seed?: string
  }>(),
  {
    socials: false,
    kindEditable: false,
    expanded: true,
    adding: false,
    placeholder: "",
    autofocus: true,
    seed: "",
  }
)

const emit = defineEmits<{
  update: [row: ContactRow]
  enter: []
  toggle: []
}>()

const icon = computed(() => contactIcon(props.row))
const kindLabel = computed(
  () => CONTACT_KINDS.find((k) => k.key === props.row.contact_type)?.label ?? "Contact"
)

// --- Writing the row ------------------------------------------------------------

function emitRow(changes: Partial<ContactRow>) {
  emit("update", { ...props.row, ...changes })
}

function setKind(kind: ContactKind) {
  emitRow({ contact_type: kind })
  if (typedText.value) onTyped(typedText.value, kind)
}

// --- Typing it ------------------------------------------------------------------

/** What's in the box while someone types; null shows the row's own value. */
const typedText = ref<string | null>(null)
const typed = computed(() => typedText.value ?? props.row.value ?? "")

/** A typed value, normalized for its kind; null when it doesn't read. */
function read(text: string, kind: string): string | null {
  const field = { type: props.socials ? "socials" : kind } as Field
  return text.trim() ? (parseValue(field, text) as string | null) : null
}

const error = computed(() => {
  const text = typedText.value
  if (!text?.trim() || read(text, props.row.contact_type)) return null
  return `Not a valid ${kindLabel.value.toLowerCase()}`
})

function onTyped(text: string, kind: string = props.row.contact_type) {
  typedText.value = text
  const value = read(text, kind)
  if (!value) return
  emitRow({
    contact_type: kind,
    value,
    ...(props.socials ? { label: socialPlatformOf(value) } : {}),
  })
}

// --- The line -------------------------------------------------------------------

/** A click on the line itself, not its box or a button, opens or closes it. */
function onHeadClick(event: MouseEvent) {
  if ((event.target as HTMLElement).closest("input, button, select, a")) return
  emit("toggle")
}

const box = ref<HTMLInputElement | null>(null)

onMounted(() => {
  if (props.seed) onTyped(props.seed)
  if (!props.autofocus) return
  nextTick(() => {
    const input = box.value
    if (!input) return
    input.focus()
    // A seeded key is the start of what's typed; a stored value is replaced.
    if (props.seed) input.setSelectionRange(input.value.length, input.value.length)
    else input.select()
  })
})
</script>

<style>
/* The line, its fields panel, and their boxes are RowsEditor's; these are
   the inputs particular to a contact. */
.contact-row-editor {
  display: flex;
  flex-direction: column;

  .typed {
    color: var(--auger-link);
  }

  .label {
    flex: 1 1 10em;
    min-width: 0;
  }
}
</style>
